import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Header from "./Header";
import Footer from "./Footer";
import { apiJson, assetUrl } from "../utils/api";
import useSeo from "../hooks/useSeo";

const TYPE_LABEL = {
  solo_lab: "单人实验",
  joint: "共同实验",
  training: "关系训练",
};

export default function RelationshipMemoryPage() {
  const navigate = useNavigate();
  const userId = Number(localStorage.getItem("user_id"));
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useSeo({
    title: "关系记忆看板｜回顾你们走过的实验与成长",
    metaDescription:
      "跨会话汇总关系模拟、共同实验、关系训练与行为观察，帮助你看见长期相处痕迹。",
    path: "/ai/relationship-memory",
  });

  useEffect(() => {
    if (!userId) {
      navigate(
        `/login?redirect=${encodeURIComponent("/ai/relationship-memory")}`,
        { replace: true }
      );
    }
  }, [userId, navigate]);

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setError("");
    try {
      const { res, data } = await apiJson(
        `/api/ai/relationship-memory?user_id=${userId}`
      );
      if (!res.ok) throw new Error(data?.detail || "加载失败");
      setBoard(data);
    } catch (err) {
      setError(err.message || "加载失败");
      setBoard(null);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    load();
  }, [load]);

  const stats = board?.stats || {};
  const links = board?.links || {};

  return (
    <div className="min-h-screen bg-[#f7f4ef] flex flex-col">
      <Header />
      <main className="flex-1 w-full mx-auto px-4 py-8 max-w-5xl space-y-6">
        <section className="rounded-3xl bg-gradient-to-br from-violet-50 via-rose-50 to-amber-50 border border-white p-6 md:p-8 space-y-3">
          <p className="text-sm text-violet-600 font-medium">长期关系记忆</p>
          <h1 className="text-3xl font-extrabold text-gray-900">关系记忆看板</h1>
          <p className="text-sm text-gray-600 max-w-2xl leading-relaxed">
            把分散在实验、训练、陪伴里的痕迹收在一起——不是档案柜，而是「你们最近发生了什么」。
          </p>
          <div className="flex flex-wrap gap-2 pt-1">
            <Link
              to={links.simulation || "/ai/simulation"}
              className="px-3 py-1.5 rounded-full bg-white border text-xs"
            >
              关系模拟
            </Link>
            <Link
              to={links.growth || "/ai/relationship-growth"}
              className="px-3 py-1.5 rounded-full bg-white border text-xs"
            >
              成长追踪
            </Link>
            <Link
              to={links.behavior || "/ai/behavior-profile"}
              className="px-3 py-1.5 rounded-full bg-white border text-xs"
            >
              行为画像
            </Link>
            <Link
              to={links.companion || "/ai/consultant?tab=chat"}
              className="px-3 py-1.5 rounded-full bg-white border text-xs"
            >
              和我聊聊
            </Link>
          </div>
        </section>

        {loading && (
          <p className="text-sm text-gray-500 text-center py-10">加载中…</p>
        )}
        {error && (
          <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            {error}
          </div>
        )}

        {board && !loading && (
          <>
            <section className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { label: "完成实验", value: stats.solo_labs },
                { label: "关系训练", value: stats.trainings },
                { label: "共同对象", value: stats.partners },
                { label: "行为样本", value: stats.behavior_samples },
              ].map((s) => (
                <div
                  key={s.label}
                  className="rounded-2xl bg-white border px-4 py-3 text-center"
                >
                  <p className="text-2xl font-bold text-violet-700 tabular-nums">
                    {s.value ?? 0}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">{s.label}</p>
                </div>
              ))}
            </section>

            {(board.highlights || []).length > 0 && (
              <section className="rounded-2xl bg-white border p-5 space-y-2">
                <h2 className="font-semibold text-gray-900">近期亮点</h2>
                <ul className="space-y-2">
                  {board.highlights.map((h) => (
                    <li
                      key={h}
                      className="text-sm text-gray-700 leading-relaxed pl-3 border-l-2 border-violet-300"
                    >
                      {h}
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {(board.partners || []).length > 0 && (
              <section className="rounded-2xl bg-white border p-5 space-y-3">
                <h2 className="font-semibold text-gray-900">和谁一起走过</h2>
                <div className="grid sm:grid-cols-2 gap-3">
                  {board.partners.map((p) => (
                    <Link
                      key={p.id}
                      to={p.href}
                      className="flex gap-3 rounded-xl border px-3 py-3 hover:border-violet-300 transition"
                    >
                      <img
                        src={assetUrl(p.avatar) || "/default-avatar.png"}
                        alt=""
                        className="w-11 h-11 rounded-full object-cover bg-gray-100"
                      />
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-gray-900 truncate">
                          {p.name}
                        </p>
                        <p className="text-[11px] text-gray-500">
                          {p.growth_level || "起步"} · 实验{" "}
                          {p.experiment_count ?? 0} 次
                        </p>
                        {p.latest_note && (
                          <p className="text-xs text-gray-600 mt-1 line-clamp-2">
                            {p.latest_note}
                          </p>
                        )}
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}

            <div className="grid md:grid-cols-2 gap-4">
              {(board.recurring_patterns || []).length > 0 && (
                <section className="rounded-2xl bg-white border p-5 space-y-2">
                  <h2 className="font-semibold text-gray-900">反复出现的模式</h2>
                  {board.recurring_patterns.map((p) => (
                    <div
                      key={p.title}
                      className="flex justify-between text-sm border-b border-gray-50 py-1.5"
                    >
                      <span className="text-gray-700">{p.title}</span>
                      <span className="text-violet-600 text-xs">×{p.count}</span>
                    </div>
                  ))}
                </section>
              )}

              <section className="rounded-2xl bg-white border p-5 space-y-2">
                <h2 className="font-semibold text-gray-900">行为观察快照</h2>
                {board.behavior_snapshot?.exists ? (
                  <>
                    {(board.behavior_snapshot.strengths || []).map((s) => (
                      <p key={s} className="text-xs text-emerald-800">
                        · {s}
                      </p>
                    ))}
                    {(board.behavior_snapshot.challenges || []).map((s) => (
                      <p key={s} className="text-xs text-amber-800">
                        · {s}
                      </p>
                    ))}
                    {board.behavior_snapshot.growth && (
                      <p className="text-xs text-gray-600 mt-2 leading-relaxed">
                        {board.behavior_snapshot.growth}
                      </p>
                    )}
                  </>
                ) : (
                  <p className="text-xs text-gray-500">
                    授权「和我聊聊」后，这里会逐渐出现你的关系行为观察。
                  </p>
                )}
                <Link
                  to={links.behavior || "/ai/behavior-profile"}
                  className="inline-block text-xs text-violet-600 underline mt-1"
                >
                  查看完整画像
                </Link>
              </section>
            </div>

            {(board.discuss_topics || []).length > 0 && (
              <section className="rounded-2xl border border-amber-100 bg-amber-50/70 p-5 space-y-2">
                <h2 className="font-semibold text-amber-950">值得提前聊的话题</h2>
                <ul className="space-y-1.5">
                  {board.discuss_topics.map((t) => (
                    <li key={t} className="text-sm text-amber-950/90">
                      · {t}
                    </li>
                  ))}
                </ul>
              </section>
            )}

            <section className="rounded-2xl bg-white border p-5 space-y-3">
              <h2 className="font-semibold text-gray-900">关系时间线</h2>
              {(board.timeline || []).length === 0 ? (
                <p className="text-sm text-gray-500">
                  还没有记录。去完成一次关系模拟或训练后，会显示在这里。
                </p>
              ) : (
                <ul className="space-y-3">
                  {board.timeline.map((ev, i) => (
                    <li key={`${ev.type}-${ev.id}-${i}`}>
                      <Link
                        to={ev.href || "/ai/simulation"}
                        className="block rounded-xl border px-3 py-3 hover:border-violet-300 transition"
                      >
                        <div className="flex flex-wrap items-center gap-2 text-[11px] text-gray-400">
                          <span className="px-1.5 py-0.5 rounded bg-violet-50 text-violet-700">
                            {TYPE_LABEL[ev.type] || ev.type}
                          </span>
                          {ev.at && <span>{String(ev.at).slice(0, 16).replace("T", " ")}</span>}
                          {ev.status && <span>· {ev.status}</span>}
                        </div>
                        <p className="text-sm font-medium text-gray-900 mt-1">
                          {ev.title}
                          {ev.partner?.name ? ` · ${ev.partner.name}` : ""}
                        </p>
                        {ev.summary && (
                          <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">
                            {ev.summary}
                          </p>
                        )}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            {(board.personal_notes || []).length > 0 && (
              <section className="rounded-2xl bg-white border p-5 space-y-2">
                <div className="flex justify-between items-center">
                  <h2 className="font-semibold text-gray-900">个人关系笔记</h2>
                  <Link
                    to={links.ai_memory || "/center/ai-memory"}
                    className="text-xs text-violet-600 underline"
                  >
                    管理 AI 记忆
                  </Link>
                </div>
                {board.personal_notes.map((n) => (
                  <p key={n.id} className="text-xs text-gray-600 leading-relaxed">
                    <span className="text-violet-500">[{n.type}]</span> {n.content}
                  </p>
                ))}
              </section>
            )}

            <p className="text-[11px] text-gray-400 leading-relaxed">
              {board.disclaimer}
            </p>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}
